import AddIcon from "@mui/icons-material/Add";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import FilterListIcon from "@mui/icons-material/FilterList";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import SearchIcon from "@mui/icons-material/Search";
import StarIcon from "@mui/icons-material/Star";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import {
  Badge,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  Menu,
  MenuItem,
  Popover,
  Select,
  Switch,
  TablePagination,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { ApiRequestError } from "../api/client.tsx";
import { ViewToggle } from "../../../components/aiDetectionSections/ViewToggle.tsx";
import { GLOBAL_TOOLTIP_PROPS } from "../../../config/tooltips.config.ts";
import { isPrivilegedRoleOption, isPrivilegedSystemRole } from "../../../utils/appRole.js";
import { useModuleCrudPermissions } from "../../../hooks/useModuleCrudPermissions.js";
import { resolveUserFormRoleId } from "../api/adminUsersRolesClient.ts";
import {
  createUser,
  deleteUser,
  fetchActiveRoleOptions,
  fetchUserDetail,
  fetchUsers,
  patchUserStatus,
  updateUser,
} from "../store/localUserRoleStore.tsx";
import type {
  AppUserRole,
  DateFilterPreset,
  UserListItem,
  UserRoleOption,
} from "../types.tsx";
import { IconUser, IconUsers } from "../layout/icons.tsx";
import { RolesPermissionsSection } from "../roles/RolesPermissionsSection.tsx";
import { DataTable, type RowMenuItem, type SortDir, type TableColumn } from "../table-page/DataTable.tsx";
import { StandardModal } from "../table-page/StandardModal.tsx";
import { UserFormModal, type UserFormValues } from "./UserFormModal.tsx";

const UM_FILTER_SELECT_PR_CLEAR = "76px";
const UM_FILTER_CLEAR_BTN_RIGHT_PX = 36;
const LS_UM_VIEW_MODE = "administration-user-mgmt-view-mode";
const LS_UM_FAVORITES = "administration-user-mgmt-favorite-ids";

function loadFavoriteUserIds(): string[] {
  try {
    const raw = localStorage.getItem(LS_UM_FAVORITES);
    if (!raw) return [];
    const arr = JSON.parse(raw) as unknown;
    return Array.isArray(arr) ? arr.map(String) : [];
  } catch {
    return [];
  }
}

function saveFavoriteUserIds(ids: string[]) {
  try {
    localStorage.setItem(LS_UM_FAVORITES, JSON.stringify(ids));
  } catch {
    /* ignore */
  }
}

function loadUserMgmtViewMode(): "list" | "grid" {
  try {
    const v = localStorage.getItem(LS_UM_VIEW_MODE);
    if (v === "grid" || v === "list") return v;
  } catch {
    /* ignore */
  }
  return "list";
}

const DATE_OPTIONS: { value: DateFilterPreset; label: string }[] = [
  { value: "all", label: "All dates" },
  { value: "today", label: "Today" },
  { value: "yesterday", label: "Yesterday" },
  { value: "last7", label: "Last 7 days" },
  { value: "last30", label: "Last 30 days" },
  { value: "thisWeek", label: "This Week" },
  { value: "thisMonth", label: "This Month" },
  { value: "custom", label: "Custom Range" },
];

function formatCreated(iso: string): string {
  try {
    const d = new Date(iso);
    const mon = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"][d.getMonth()];
    return `${d.getDate()}${mon ?? ""}${String(d.getFullYear()).slice(-2)}`;
  } catch {
    return iso;
  }
}

function shortUserId(id: string): string {
  return id.replace(/-/g, "").slice(0, 6).toUpperCase();
}

function roleLabel(role: AppUserRole): string {
  return role === "admin" ? "Admin" : "End User";
}

function userRoleLabel(row: UserListItem): string {
  if (row.businessRoleName?.trim()) return row.businessRoleName.trim();
  if (row.systemRoleLabel?.trim()) return row.systemRoleLabel.trim();
  return roleLabel(row.role);
}

function mapSortColumn(id: string | null): "role" | "name" | "email" | "createdAt" | "status" {
  if (id === "name") return "name";
  if (id === "email") return "email";
  if (id === "created") return "createdAt";
  if (id === "status") return "status";
  return "role";
}

function validateForm(
  mode: "create" | "edit",
  v: UserFormValues,
  camerasRequired: boolean,
  roleOptions: UserRoleOption[],
): Partial<Record<keyof UserFormValues | "form", string>> {
  const e: Partial<Record<keyof UserFormValues | "form", string>> = {};
  if (!v.firstName.trim()) e.firstName = "First name is required";
  if (!v.lastName.trim()) e.lastName = "Last name is required";
  if (!v.email.trim()) e.email = "Email is required";
  else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.email.trim())) e.email = "Enter a valid email";
  if (!v.roleId.trim()) e.roleId = "Role is required";
  else if (roleOptions.length > 0 && !roleOptions.some((o) => o.id === v.roleId)) {
    e.roleId = "Select a valid role";
  }
  if (mode === "create" && !v.password) e.password = "Password is required";
  if (camerasRequired && v.cameraIds.length === 0) {
    e.cameraIds = "Select at least one camera for view access.";
  }
  return e;
}

export type DashboardUserManagementSectionProps = {
  activeTab: "users" | "roles";
};

export function DashboardUserManagementSection({ activeTab }: DashboardUserManagementSectionProps) {
  const { create: canCreate, update: canUpdate, delete: canDelete, hasRowActions } =
    useModuleCrudPermissions("users");
  const [items, setItems] = useState<UserListItem[]>([]);
  const [roleOptions, setRoleOptions] = useState<UserRoleOption[]>([]);
  const [formCamerasRequired, setFormCamerasRequired] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [searchInput, setSearchInput] = useState("");
  const [searchQuery, setSearchQuery] = useState("");

  const [viewMode, setViewMode] = useState<"list" | "grid">(loadUserMgmtViewMode);
  const [showFavoritesOnly, setShowFavoritesOnly] = useState(false);
  const [favoriteUserIds, setFavoriteUserIds] = useState<string[]>(loadFavoriteUserIds);
  const [gridMenuAnchor, setGridMenuAnchor] = useState<null | HTMLElement>(null);
  const [gridMenuRow, setGridMenuRow] = useState<UserListItem | null>(null);

  const [dateFilter, setDateFilter] = useState<DateFilterPreset>("all");
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");

  const [selectedRoles, setSelectedRoles] = useState<Set<string>>(() => new Set());
  const [roleAnchor, setRoleAnchor] = useState<HTMLElement | null>(null);

  const [statusFilter, setStatusFilter] = useState<"" | "active" | "inactive">("");

  const [sortColumnId, setSortColumnId] = useState<string | null>("role");
  const [sortDir, setSortDir] = useState<SortDir>("asc");

  const [formOpen, setFormOpen] = useState(false);
  const [formMode, setFormMode] = useState<"create" | "edit">("create");
  const [editing, setEditing] = useState<UserListItem | null>(null);
  const [saveBusy, setSaveBusy] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<keyof UserFormValues | "form", string>>>({});

  const [deleteTarget, setDeleteTarget] = useState<UserListItem | null>(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  useEffect(() => {
    try {
      localStorage.setItem(LS_UM_VIEW_MODE, viewMode);
    } catch {
      /* ignore */
    }
  }, [viewMode]);

  useEffect(() => {
    saveFavoriteUserIds(favoriteUserIds);
  }, [favoriteUserIds]);

  useEffect(() => {
    const t = window.setTimeout(() => setSearchQuery(searchInput), 300);
    return () => window.clearTimeout(t);
  }, [searchInput]);

  const rolesQuery = useMemo(() => {
    if (selectedRoles.size === 0 || selectedRoles.size === roleOptions.length) return undefined;
    return Array.from(selectedRoles);
  }, [roleOptions.length, selectedRoles]);

  const roleButtonLabel =
    selectedRoles.size === 0 || selectedRoles.size === roleOptions.length
      ? "All Roles"
      : roleOptions
          .filter((r) => selectedRoles.has(r.id))
          .map((r) => r.role)
          .join(", ");

  const loadUsers = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const data = await fetchUsers({
        page,
        limit: pageSize,
        search: searchQuery || undefined,
        roles: rolesQuery,
        status: statusFilter || undefined,
        dateFilter,
        dateFrom: dateFilter === "custom" ? customFrom : undefined,
        dateTo: dateFilter === "custom" ? customTo : undefined,
        sortBy: mapSortColumn(sortColumnId),
        sortDir,
        favoritesOnly: showFavoritesOnly,
        favoriteUserIds,
      });
      setItems(data.items);
      setRoleOptions(data.roleOptions);
      setTotal(data.total);
    } catch {
      setLoadError("Unable to load users.");
      setItems([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [
    page,
    pageSize,
    searchQuery,
    rolesQuery,
    statusFilter,
    dateFilter,
    customFrom,
    customTo,
    sortColumnId,
    sortDir,
    showFavoritesOnly,
    favoriteUserIds,
  ]);

  useEffect(() => {
    if (activeTab !== "users") return;
    void loadUsers();
  }, [activeTab, loadUsers]);

  useEffect(() => {
    setPage(1);
  }, [searchQuery, rolesQuery, statusFilter, dateFilter, customFrom, customTo, sortColumnId, sortDir, showFavoritesOnly, favoriteUserIds]);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const safePage = Math.min(page, totalPages);

  const patchRow = useCallback((updated: UserListItem) => {
    setItems((rows) => rows.map((r) => (r.id === updated.id ? updated : r)));
  }, []);

  const handleToggleFavoriteUser = useCallback((id: string) => {
    setFavoriteUserIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  }, []);

  const favoritesCount = favoriteUserIds.length;

  const handleToggleActive = useCallback(
    async (row: UserListItem, next: boolean) => {
      try {
        const u = await patchUserStatus(row.id, next);
        patchRow(u);
      } catch (e) {
        const msg = e instanceof ApiRequestError ? e.message : "Could not update status";
        window.alert(msg);
        void loadUsers();
      }
    },
    [loadUsers, patchRow],
  );

  const openCreate = useCallback(() => {
    setFormMode("create");
    setEditing(null);
    setFormCamerasRequired(true);
    setSaveError(null);
    setFieldErrors({});
    void fetchActiveRoleOptions()
      .then((opts) => {
        if (opts.length === 0) return;
        setRoleOptions((prev) => {
          const byId = new Map(prev.map((o) => [o.id, o]));
          for (const o of opts) {
            const existing = byId.get(o.id);
            if (!existing) {
              byId.set(o.id, o);
              continue;
            }
            byId.set(o.id, {
              ...existing,
              ...o,
              role: o.role || existing.role,
              systemRole: existing.systemRole ?? o.systemRole,
              slug: existing.slug ?? o.slug,
              isPredefined:
                existing.isPredefined !== undefined
                  ? existing.isPredefined
                  : o.isPredefined,
            });
          }
          return Array.from(byId.values());
        });
      })
      .catch(() => {});
    setFormOpen(true);
  }, []);

  const openEdit = useCallback(
    (row: UserListItem) => {
      setFormMode("edit");
      setEditing(row);
      const roleId = resolveUserFormRoleId(row, roleOptions);
      setFormCamerasRequired(
        !isPrivilegedSystemRole(row.systemRoleLabel) && !isPrivilegedRoleOption(roleId, roleOptions),
      );
      setSaveError(null);
      setFieldErrors({});
      setFormOpen(true);
      void fetchUserDetail(row.id)
        .then((detail) => {
          if (!detail) return;
          const resolvedRoleId = resolveUserFormRoleId(detail, roleOptions);
          setEditing({
            ...detail,
            businessRoleId: resolvedRoleId || detail.businessRoleId,
          });
          setFormCamerasRequired(
            !isPrivilegedSystemRole(detail.systemRoleLabel) &&
              !isPrivilegedRoleOption(resolvedRoleId, roleOptions),
          );
        })
        .catch(() => {});
    },
    [roleOptions],
  );

  const closeForm = useCallback(() => {
    setFormOpen(false);
  }, []);

  const submitForm = useCallback(
    async (values: UserFormValues) => {
      const adminRole = isPrivilegedRoleOption(values.roleId, roleOptions);
      const camerasRequired = !adminRole;
      const errs = validateForm(formMode, values, camerasRequired, roleOptions);
      setFieldErrors(errs);
      if (Object.keys(errs).length) return;

      setSaveBusy(true);
      setSaveError(null);
      try {
        const cameraIds = adminRole ? [] : values.cameraIds;
        if (formMode === "create") {
          await createUser({
            firstName: values.firstName.trim(),
            lastName: values.lastName.trim(),
            roleId: values.roleId,
            email: values.email.trim().toLowerCase(),
            password: values.password,
            cameraIds,
          });
        } else if (editing) {
          await updateUser(editing.id, {
            firstName: values.firstName.trim(),
            lastName: values.lastName.trim(),
            roleId: values.roleId,
            email: values.email.trim().toLowerCase(),
            password: values.password.trim() || undefined,
            cameraIds,
          });
        }
        setFormOpen(false);
        void loadUsers();
      } catch (e) {
        if (e instanceof ApiRequestError) {
          if (e.code === "EMAIL_EXISTS") setSaveError("Email already exists.");
          else if (e.code === "VALIDATION_ERROR") setSaveError(e.message);
          else setSaveError(e.message);
        } else {
          setSaveError("Something went wrong.");
        }
      } finally {
        setSaveBusy(false);
      }
    },
    [editing, formMode, loadUsers, roleOptions],
  );

  const runDelete = useCallback(async () => {
    if (!deleteTarget) return;
    setDeleteBusy(true);
    setDeleteError(null);
    try {
      await deleteUser(deleteTarget.id);
      setFavoriteUserIds((prev) => prev.filter((id) => id !== deleteTarget.id));
      setDeleteTarget(null);
      void loadUsers();
    } catch (e) {
      setDeleteError(e instanceof ApiRequestError ? e.message : "Delete failed");
    } finally {
      setDeleteBusy(false);
    }
  }, [deleteTarget, loadUsers]);

  const rowMenuItems: RowMenuItem<UserListItem>[] = useMemo(
    () => [
      ...(canUpdate
        ? [
            {
              id: "edit",
              label: "Edit",
              icon: <EditOutlinedIcon fontSize="small" />,
              onSelect: (row: UserListItem) => openEdit(row),
            },
          ]
        : []),
      ...(canDelete
        ? [
            {
              id: "delete",
              label: "Delete",
              icon: <DeleteOutlinedIcon fontSize="small" />,
              tone: "danger" as const,
              onSelect: (row: UserListItem) => {
                setDeleteError(null);
                setDeleteTarget(row);
              },
            },
          ]
        : []),
    ],
    [canUpdate, canDelete, openEdit],
  );

  const columns: TableColumn<UserListItem>[] = useMemo(
    () => [
      {
        id: "id",
        header: "User ID",
        renderCell: (row) => (
          <span style={{ fontFamily: "ui-monospace, monospace", fontSize: 13 }}>{shortUserId(row.id)}</span>
        ),
        getTitle: (row) => row.id,
      },
      {
        id: "name",
        header: "Name",
        primary: true,
        sortable: true,
        renderCell: (row) => {
          const fav = favoriteUserIds.includes(row.id);
          return (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, minWidth: 0 }}>
              <IconUser className="ds-text-secondary" style={{ flexShrink: 0 }} />
              <span
                style={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  flex: 1,
                  minWidth: 0,
                }}
              >
                {row.fullName?.trim() || "—"}
              </span>
              <IconButton
                size="small"
                aria-label={fav ? "Remove from favorites" : "Add to favorites"}
                onClick={(e) => {
                  e.stopPropagation();
                  handleToggleFavoriteUser(row.id);
                }}
                sx={{ flexShrink: 0, p: 0.25 }}
              >
                {fav ? (
                  <StarIcon sx={{ fontSize: 18, color: "#FF8555" }} />
                ) : (
                  <StarBorderIcon sx={{ fontSize: 18, color: "#9CA3AF" }} />
                )}
              </IconButton>
            </Box>
          );
        },
        getTitle: (row) => row.fullName?.trim() || "",
      },
      {
        id: "role",
        header: "Role",
        sortable: true,
        headerLeading: () => (
          <span className="ds-text-secondary" style={{ display: "inline-flex", width: 16, height: 16 }}>
            <IconUser />
          </span>
        ),
        renderCell: (row) => userRoleLabel(row),
        getTitle: (row) => userRoleLabel(row),
      },
      {
        id: "email",
        header: "Email",
        sortable: true,
        renderCell: (row) => row.email,
      },
      {
        id: "password",
        header: "Password",
        renderCell: (row) => (
          <span style={{ fontFamily: "ui-monospace, monospace", fontSize: 13 }}>{row.passwordDisplay}</span>
        ),
      },
      {
        id: "created",
        header: "Created Date",
        sortable: true,
        renderCell: (row) => formatCreated(row.createdAt),
      },
      {
        id: "status",
        header: "Status",
        sortable: true,
        align: "center",
        minWidthPx: 168,
        renderCell: (row) => (
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "center",
              gap: 8,
              flexWrap: "nowrap",
            }}
          >
            <span className={row.isActive ? "ds-pill ds-pill--accent" : "ds-pill"}>
              {row.isActive ? "Active" : "Inactive"}
            </span>
            <Switch
              size="small"
              checked={row.isActive}
              disabled={!canUpdate}
              onChange={(_, c) => void handleToggleActive(row, c)}
              title={`Toggle active state for ${row.email}`}
            />
          </div>
        ),
      },
    ],
    [handleToggleActive, favoriteUserIds, handleToggleFavoriteUser, canUpdate],
  );

  const deleteFooter = useMemo(
    () => (
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
        <button type="button" className="ds-btn ds-btn--ghost" onClick={() => setDeleteTarget(null)} disabled={deleteBusy}>
          Cancel
        </button>
        <button type="button" className="ds-btn ds-btn--danger" onClick={() => void runDelete()} disabled={deleteBusy}>
          Delete
        </button>
      </div>
    ),
    [deleteBusy, runDelete],
  );

  if (activeTab === "roles") {
    return <RolesPermissionsSection />;
  }

  return (
    <>
      <Box
        sx={{
          position: "relative",
          display: "flex",
          flexDirection: "column",
          flex: 1,
          minHeight: 0,
          width: "100%",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            gap: 1,
            pb: 2,
            borderBottom: "1px solid #E5E7EB",
          }}
        >
          <TextField
            size="small"
            placeholder="Search users…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            sx={{
              width: { xs: "100%", sm: 240 },
              minWidth: { xs: 0, sm: 240 },
              flex: { xs: "1 1 100%", sm: "0 0 auto" },
              "& .MuiOutlinedInput-root": {
                height: 36,
                fontFamily: "Roboto, sans-serif",
                fontSize: 14,
                bgcolor: "#fff",
                borderRadius: "10px",
                "& fieldset": { borderColor: "#E5E7EB" },
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 20, color: "#9E9E9E" }} />
                </InputAdornment>
              ),
            }}
          />
          <Tooltip
            title={showFavoritesOnly ? "Show all users" : "Show favorites only"}
            {...GLOBAL_TOOLTIP_PROPS}
          >
            <IconButton
              size="small"
              onClick={() => {
                setShowFavoritesOnly((v) => !v);
                setPage(1);
              }}
              sx={{
                border: "1px solid #E5E7EB",
                borderRadius: "10px",
                height: 36,
                width: 36,
                bgcolor: showFavoritesOnly ? "#FFE0D0" : "#fff",
                "&:hover": {
                  bgcolor: showFavoritesOnly ? "#FFD0B8" : "#F5F5F5",
                },
              }}
            >
              <Badge
                badgeContent={favoritesCount}
                color="primary"
                sx={{
                  "& .MuiBadge-badge": {
                    fontSize: "10px",
                    height: "16px",
                    minWidth: "16px",
                    bgcolor: "#2932E5",
                    borderRadius: "10px",
                  },
                }}
              >
                {showFavoritesOnly ? (
                  <StarIcon fontSize="small" sx={{ color: "#FF8555" }} />
                ) : (
                  <StarBorderIcon fontSize="small" sx={{ color: "#9E9E9E" }} />
                )}
              </Badge>
            </IconButton>
          </Tooltip>
          <ViewToggle
            view={viewMode}
            onChange={(v) => {
              setViewMode(v);
              setPage(1);
            }}
            ariaLabel="Toggle users view mode"
          />
          <FormControl
            size="small"
            sx={{ minWidth: { xs: 120, sm: 140 }, position: "relative" }}
          >
            <Select
              value={dateFilter}
              onChange={(e) => {
                setDateFilter(e.target.value as DateFilterPreset);
                setPage(1);
              }}
              displayEmpty
              sx={{
                height: 36,
                fontFamily: "Roboto, sans-serif",
                fontSize: 14,
                bgcolor: dateFilter !== "all" ? "#FFE0D0" : "#fff",
                borderRadius: "10px",
                "& .MuiSelect-select": {
                  display: "flex",
                  alignItems: "center",
                  paddingRight:
                    dateFilter !== "all"
                      ? `${UM_FILTER_SELECT_PR_CLEAR} !important`
                      : undefined,
                },
                "& .MuiOutlinedInput-notchedOutline": { borderColor: "#E5E7EB" },
                "&:hover": {
                  bgcolor: dateFilter !== "all" ? "#FFE0D0" : "#f5f5f5",
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#2932E5",
                },
              }}
              startAdornment={
                <CalendarTodayIcon sx={{ fontSize: 18, color: "#9E9E9E", mr: 1 }} />
              }
            >
              {DATE_OPTIONS.map((o) => (
                <MenuItem key={o.value} value={o.value}>
                  {o.label}
                </MenuItem>
              ))}
            </Select>
            {dateFilter !== "all" ? (
              <IconButton
                size="small"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setDateFilter("all");
                  setPage(1);
                }}
                aria-label="Clear date filter"
                sx={{
                  position: "absolute",
                  right: `${UM_FILTER_CLEAR_BTN_RIGHT_PX}px`,
                  top: "50%",
                  transform: "translateY(-50%)",
                  zIndex: 2,
                  p: 0.25,
                  width: 28,
                  height: 28,
                  "&:hover": { bgcolor: "rgba(0,0,0,0.04)" },
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            ) : null}
          </FormControl>
          {dateFilter === "custom" ? (
            <>
              <TextField
                type="date"
                size="small"
                value={customFrom}
                onChange={(e) => setCustomFrom(e.target.value)}
                sx={{ "& .MuiOutlinedInput-root": { height: 36, borderRadius: "10px" } }}
                InputLabelProps={{ shrink: true }}
                label="From"
              />
              <TextField
                type="date"
                size="small"
                value={customTo}
                onChange={(e) => setCustomTo(e.target.value)}
                sx={{ "& .MuiOutlinedInput-root": { height: 36, borderRadius: "10px" } }}
                InputLabelProps={{ shrink: true }}
                label="To"
              />
            </>
          ) : null}
          <Button
            variant="outlined"
            onClick={(e) => setRoleAnchor(e.currentTarget)}
            aria-expanded={Boolean(roleAnchor)}
            aria-haspopup="true"
            sx={{
              height: 36,
              textTransform: "none",
              fontFamily: "Roboto, sans-serif",
              fontSize: 14,
              borderRadius: "10px",
              borderColor: "#E5E7EB",
              color: "#212121",
              bgcolor:
                selectedRoles.size > 0 &&
                selectedRoles.size < roleOptions.length
                  ? "#FFE0D0"
                  : "#fff",
              "&:hover": { borderColor: "#2932E5", bgcolor: "#f5f5f5" },
            }}
            startIcon={<FilterListIcon sx={{ fontSize: 18, color: "#9E9E9E" }} />}
          >
            {roleButtonLabel}
          </Button>
          <Popover
            open={Boolean(roleAnchor)}
            anchorEl={roleAnchor}
            onClose={() => setRoleAnchor(null)}
            anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
            slotProps={{ paper: { sx: { p: 2, minWidth: 220 } } }}
          >
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={
                      selectedRoles.size === 0 ||
                      selectedRoles.size === roleOptions.length
                    }
                    indeterminate={
                      selectedRoles.size > 0 &&
                      selectedRoles.size < roleOptions.length
                    }
                    onChange={() => {
                      setSelectedRoles(new Set());
                    }}
                  />
                }
                label="All roles"
              />
              {roleOptions.map((r) => (
                <FormControlLabel
                  key={r.id}
                  control={
                    <Checkbox
                      checked={
                        selectedRoles.size === 0 || selectedRoles.has(r.id)
                      }
                      onChange={() => {
                        setSelectedRoles((prev) => {
                          const n =
                            prev.size === 0
                              ? new Set(roleOptions.map((o) => o.id))
                              : new Set(prev);
                          if (n.has(r.id)) n.delete(r.id);
                          else n.add(r.id);
                          if (n.size === 0 || n.size === roleOptions.length)
                            return new Set();
                          return n;
                        });
                      }}
                    />
                  }
                  label={r.role}
                />
              ))}
            </Box>
          </Popover>
          <FormControl
            size="small"
            sx={{ minWidth: 130, position: "relative" }}
          >
            <Select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value as "" | "active" | "inactive");
                setPage(1);
              }}
              displayEmpty
              renderValue={(v) =>
                v === "" ? "All Status" : v === "active" ? "Active" : "Inactive"
              }
              sx={{
                height: 36,
                fontFamily: "Roboto, sans-serif",
                fontSize: 14,
                bgcolor: statusFilter !== "" ? "#FFE0D0" : "#fff",
                borderRadius: "10px",
                "& .MuiSelect-select": {
                  paddingRight:
                    statusFilter !== ""
                      ? `${UM_FILTER_SELECT_PR_CLEAR} !important`
                      : undefined,
                },
                "& .MuiOutlinedInput-notchedOutline": { borderColor: "#E5E7EB" },
                "&:hover": {
                  bgcolor: statusFilter !== "" ? "#FFE0D0" : "#f5f5f5",
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#2932E5",
                },
              }}
              startAdornment={
                <FilterListIcon sx={{ fontSize: 18, color: "#9E9E9E", mr: 1 }} />
              }
            >
              <MenuItem value="">All Status</MenuItem>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="inactive">Inactive</MenuItem>
            </Select>
            {statusFilter !== "" ? (
              <IconButton
                size="small"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setStatusFilter("");
                  setPage(1);
                }}
                aria-label="Clear status filter"
                sx={{
                  position: "absolute",
                  right: `${UM_FILTER_CLEAR_BTN_RIGHT_PX}px`,
                  top: "50%",
                  transform: "translateY(-50%)",
                  zIndex: 2,
                  p: 0.25,
                  width: 28,
                  height: 28,
                  "&:hover": { bgcolor: "rgba(0,0,0,0.04)" },
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            ) : null}
          </FormControl>
          <Box sx={{ flex: 1 }} />
          {canCreate ? (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={openCreate}
            sx={{
              bgcolor: "#2932E5",
              border: "0.8px solid #2932E5",
              borderRadius: "10px",
              color: "#fff",
              textTransform: "none",
              fontFamily: "Roboto, sans-serif",
              fontSize: { xs: "13px", sm: "14px" },
              fontWeight: 500,
              height: 36,
              px: { xs: 1.5, sm: 2 },
              boxShadow: "none",
              flexShrink: 0,
              "&:hover": {
                bgcolor: "#1f28b8",
                border: "0.8px solid #1f28b8",
                boxShadow: "none",
              },
            }}
          >
            Add User
          </Button>
          ) : null}
        </Box>

        {loadError ? (
          <Box
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 2,
              p: 4,
              bgcolor: "#FAFAFA",
            }}
          >
            <Typography sx={{ color: "#4A5565", textAlign: "center" }}>
              {loadError}
            </Typography>
            <Button
              variant="contained"
              onClick={() => void loadUsers()}
              sx={{
                bgcolor: "#2932E5",
                textTransform: "none",
                "&:hover": { bgcolor: "#1f28b8" },
              }}
            >
              Retry
            </Button>
          </Box>
        ) : (
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                overflow: "auto",
                bgcolor: "#fafafa",
                border: "1px solid #E5E7EB",
                borderRadius: "8px",
              }}
            >
              {loading ? (
                <Typography sx={{ p: 3, color: "#6B7280" }}>
                  Loading users…
                </Typography>
              ) : viewMode === "list" ? (
                <DataTable<UserListItem>
                  columns={columns}
                  rows={items}
                  getRowId={(r) => r.id}
                  sortColumnId={sortColumnId}
                  sortDir={sortDir}
                  onSortChange={(col, dir) => {
                    setSortColumnId(col);
                    setSortDir(dir);
                  }}
                  selectedRowId={null}
                  onSelectRow={() => {}}
                  rowMenuItems={rowMenuItems}
                  loading={loading}
                  emptyPlaceholder={
                    <span className="ds-text-secondary">No users found.</span>
                  }
                />
              ) : (
                <Box sx={{ p: 2 }}>
                  <Grid container spacing={2}>
                    {items.length === 0 ? (
                      <Grid item xs={12}>
                        <Typography color="text.secondary">
                          No users found.
                        </Typography>
                      </Grid>
                    ) : (
                      items.map((row) => {
                        const fav = favoriteUserIds.includes(row.id);
                        return (
                          <Grid item xs={12} sm={6} md={4} key={row.id}>
                            <Card
                              variant="outlined"
                              sx={{
                                borderRadius: "14px",
                                borderColor: "#E5E7EB",
                                bgcolor: "#fff",
                                height: "100%",
                              }}
                            >
                              <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "flex-start",
                                    justifyContent: "space-between",
                                    gap: 1,
                                    mb: 1.5,
                                  }}
                                >
                                  <Box
                                    sx={{
                                      display: "flex",
                                      gap: 1,
                                      minWidth: 0,
                                      flex: 1,
                                    }}
                                  >
                                    <IconUsers className="ds-text-secondary" />
                                    <Box sx={{ minWidth: 0 }}>
                                      <Typography fontWeight={600} noWrap>
                                        {shortUserId(row.id)} —{" "}
                                        {row.fullName?.trim() || "—"}
                                      </Typography>
                                      <Typography
                                        variant="body2"
                                        color="text.secondary"
                                        noWrap
                                      >
                                        {row.email}
                                      </Typography>
                                    </Box>
                                  </Box>
                                  <Box sx={{ display: "flex", gap: 0.25 }}>
                                    <IconButton
                                      size="small"
                                      aria-label={
                                        fav
                                          ? "Remove from favorites"
                                          : "Add to favorites"
                                      }
                                      onClick={() =>
                                        handleToggleFavoriteUser(row.id)
                                      }
                                    >
                                      {fav ? (
                                        <StarIcon
                                          sx={{ fontSize: 20, color: "#FF8555" }}
                                        />
                                      ) : (
                                        <StarBorderIcon
                                          sx={{ fontSize: 20, color: "#9CA3AF" }}
                                        />
                                      )}
                                    </IconButton>
                                    {hasRowActions ? (
                                    <IconButton
                                      size="small"
                                      aria-label="Open actions"
                                      onClick={(e) => {
                                        setGridMenuAnchor(e.currentTarget);
                                        setGridMenuRow(row);
                                      }}
                                    >
                                      <MoreVertIcon fontSize="small" />
                                    </IconButton>
                                    ) : null}
                                  </Box>
                                </Box>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                  sx={{ mb: 0.5 }}
                                >
                                  Role: {userRoleLabel(row)}
                                </Typography>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                  sx={{ mb: 1.5, fontFamily: "monospace" }}
                                >
                                  Password: {row.passwordDisplay}
                                </Typography>
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    flexWrap: "wrap",
                                    gap: 1,
                                    mb: 2,
                                  }}
                                >
                                  <span
                                    className={
                                      row.isActive
                                        ? "ds-pill ds-pill--accent"
                                        : "ds-pill"
                                    }
                                  >
                                    {row.isActive ? "Active" : "Inactive"}
                                  </span>
                                  <Switch
                                    size="small"
                                    checked={row.isActive}
                                    disabled={!canUpdate}
                                    onChange={(_, c) =>
                                      void handleToggleActive(row, c)
                                    }
                                  />
                                </Box>
                                {hasRowActions ? (
                                <Box sx={{ display: "flex", gap: 1 }}>
                                  {canUpdate ? (
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    onClick={() => openEdit(row)}
                                    sx={{ textTransform: "none" }}
                                  >
                                    Edit
                                  </Button>
                                  ) : null}
                                  {canDelete ? (
                                  <Button
                                    size="small"
                                    variant="text"
                                    color="error"
                                    onClick={() => setDeleteTarget(row)}
                                    sx={{ textTransform: "none" }}
                                  >
                                    Delete
                                  </Button>
                                  ) : null}
                                </Box>
                                ) : null}
                              </CardContent>
                            </Card>
                          </Grid>
                        );
                      })
                    )}
                  </Grid>
                </Box>
              )}
            </Box>
            <TablePagination
              component="div"
              count={total}
              page={safePage - 1}
              onPageChange={(_, p) => setPage(p + 1)}
              rowsPerPage={pageSize}
              onRowsPerPageChange={(e) => {
                setPageSize(parseInt(e.target.value, 10));
                setPage(1);
              }}
              rowsPerPageOptions={[5, 10, 25, 50]}
              sx={{
                borderTop: "1px solid #E5E7EB",
                "& .MuiTablePagination-toolbar": { minHeight: 52 },
              }}
            />
          </Box>
        )}
      </Box>

      <Menu
        anchorEl={gridMenuAnchor}
        open={Boolean(gridMenuAnchor) && Boolean(gridMenuRow)}
        onClose={() => {
          setGridMenuAnchor(null);
          setGridMenuRow(null);
        }}
      >
        {canUpdate ? (
        <MenuItem
          onClick={() => {
            if (gridMenuRow) openEdit(gridMenuRow);
            setGridMenuAnchor(null);
            setGridMenuRow(null);
          }}
        >
          Edit
        </MenuItem>
        ) : null}
        {canDelete ? (
        <MenuItem
          onClick={() => {
            if (gridMenuRow) {
              setDeleteError(null);
              setDeleteTarget(gridMenuRow);
            }
            setGridMenuAnchor(null);
            setGridMenuRow(null);
          }}
        >
          Delete
        </MenuItem>
        ) : null}
      </Menu>

      <UserFormModal
        open={formOpen}
        mode={formMode}
        user={editing}
        roleOptions={roleOptions}
        camerasRequired={formCamerasRequired}
        loading={saveBusy}
        serverError={saveError}
        fieldErrors={fieldErrors}
        onClose={closeForm}
        onSubmit={(v) => void submitForm(v)}
        onRoleIdChange={(roleId) => {
          setFormCamerasRequired(!isPrivilegedRoleOption(roleId, roleOptions));
        }}
      />

      <StandardModal
        open={Boolean(deleteTarget)}
        title="Confirm Delete"
        onClose={() => {
          if (!deleteBusy) setDeleteTarget(null);
        }}
        footer={deleteFooter}
      >
        <div style={{ paddingTop: 8, paddingBottom: 8 }}>
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              gap: 8,
              color: "var(--color-error)",
              marginBottom: 16,
            }}
          >
            <WarningAmberOutlinedIcon fontSize="small" aria-hidden />
            <span className="ds-body" style={{ fontWeight: 600 }}>
              This action will delete &quot;{deleteTarget?.fullName?.trim() || deleteTarget?.email}&quot;.
            </span>
          </div>
          {deleteError ? (
            <p className="ds-error-inline" style={{ marginTop: 0 }}>
              {deleteError}
            </p>
          ) : null}
          <p className="ds-body" style={{ marginTop: 0 }}>
            Are you sure you want to delete this record?
          </p>
        </div>
      </StandardModal>
    </>
  );
}
